import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales" });

  const load = async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/auth/register", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "sales" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/auth/users/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">{t("users")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("users")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={() => setOpen(true)} data-testid="user-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("addUser")}
        </Button>
      </div>

      <div className="dashboard-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead>{t("role")}</TableHead>
              <TableHead className="text-end">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " +
                    (u.role === "admin" ? "bg-amber-50 text-amber-700" :
                     u.role === "accountant" ? "bg-indigo-50 text-indigo-700" :
                     "bg-slate-100 text-slate-700")}>
                    {t(u.role)}
                  </span>
                </TableCell>
                <TableCell className="text-end">
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-red-600">
                    <Trash2 size={15} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("addUser")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={t("name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></Field>
            <Field label={t("email")}><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></Field>
            <Field label={t("password")}><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></Field>
            <Field label={t("role")}>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                  <SelectItem value="sales">{t("sales")}</SelectItem>
                  <SelectItem value="accountant">{t("accountant")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="user-save-btn" className="bg-[#0A192F] hover:bg-[#112240] text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
